"use client";

import React, { useEffect, useState } from 'react';
import { TrendingUp, Users, Calendar, Target, ArrowUpRight, Clock, DollarSign } from 'lucide-react';
import { supabase } from '../../integrations/supabase/client';

interface Stats {
  leads: number;
  clients: number;
  workshops: number;
  revenue: number;
}

interface Lead {
  id: string;
  name: string;
  email: string;
  phone?: string;
  media_name: string;
  created_at: string;
  value?: number;
}

interface Workshop {
  id: string;
  title: string;
  date: string;
  status: string;
}

const StatCard = ({ title, value, icon, trend, color }: any) => (
  <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm card-hover">
    <div className="flex justify-between items-start mb-4">
      <div className={`p-3 rounded-2xl ${color} bg-opacity-10`}>
        {icon}
      </div>
      {trend && (
        <span className="flex items-center gap-1 text-green-600 text-xs font-bold">
          <ArrowUpRight size={14} /> {trend}
        </span>
      )}
    </div>
    <h3 className="text-charcoal-muted text-sm font-bold uppercase tracking-wider mb-1">{title}</h3>
    <p className="text-3xl font-black text-charcoal">{value}</p>
  </div>
);

const AdminDashboard = () => {
  const [stats, setStats] = useState<Stats>({ leads: 0, clients: 0, workshops: 0, revenue: 0 });
  const [recentActivity, setRecentActivity] = useState<Lead[]>([]);
  const [upcomingWorkshop, setUpcomingWorkshop] = useState<Workshop | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        // Fetch counts
        const [
          { count: leadsCount },
          { count: clientsCount },
          { count: workshopsCount },
          { data: revenueData },
          { data: recentLeads },
          { data: upcomingWorkshops }
        ] = await Promise.all([
          supabase.from('leads').select('*', { count: 'exact', head: true }),
          supabase.from('clients').select('*', { count: 'exact', head: true }),
          supabase.from('workshops').select('*', { count: 'exact', head: true }),
          supabase.from('leads').select('value').eq('status', 'closed'),
          supabase.from('leads').select('*').order('created_at', { ascending: false }).limit(5),
          supabase.from('workshops').select('*').eq('status', 'scheduled').order('date', { ascending: true }).limit(1)
        ]);
        
        const totalRevenue = revenueData?.reduce((acc, curr) => acc + (Number(curr.value) || 0), 0) || 0;

        setStats({
          leads: leadsCount || 0,
          clients: clientsCount || 0,
          workshops: workshopsCount || 0,
          revenue: totalRevenue
        });

        if (recentLeads) setRecentActivity(recentLeads);
        if (upcomingWorkshops && upcomingWorkshops.length > 0) {
          setUpcomingWorkshop(upcomingWorkshops[0]);
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();

    // Set up real-time subscription
    const subscription = supabase
      .channel('dashboard-changes')
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'leads' },
        () => fetchDashboardData()
      )
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'clients' },
        () => fetchDashboardData()
      )
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'workshops' },
        () => fetchDashboardData()
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in">
      <header>
        <h1 className="text-3xl font-black text-charcoal">Resumen Operativo</h1>
        <p className="text-charcoal-muted">Bienvenido al centro de control de MediaNews Tech Lab.</p>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard 
          title="Leads Totales" 
          value={stats.leads} 
          icon={<Target size={24} className="text-blue-600" />} 
          trend="+15%" 
          color="bg-blue-500" 
        />
        <StatCard 
          title="Clientes Activos" 
          value={stats.clients} 
          icon={<Users size={24} className="text-green-600" />} 
          trend="+2" 
          color="bg-green-500" 
        />
        <StatCard 
          title="Workshops" 
          value={stats.workshops} 
          icon={<Calendar size={24} className="text-purple-600" />} 
          color="bg-purple-500" 
        />
        <StatCard 
          title="Revenue Cerrado" 
          value={`US$${(stats.revenue / 1000).toFixed(1)}K`} 
          icon={<DollarSign size={24} className="text-orange-600" />} 
          trend="+8%" 
          color="bg-orange-500" 
        />
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 bg-white rounded-4xl border border-gray-100 p-8 shadow-sm">
          <div className="flex justify-between items-center mb-8">
            <h2 className="text-xl font-bold text-charcoal">Actividad Reciente</h2>
            <button className="text-primary text-sm font-bold hover:underline">Ver todo</button>
          </div>
          <div className="space-y-6">
            {recentActivity.length > 0 ? recentActivity.map((item, i) => (
              <div key={item.id} className="flex items-center gap-4 p-4 hover:bg-gray-50 rounded-2xl transition-colors border border-transparent hover:border-gray-100">
                <div className="p-3 rounded-xl bg-blue-50 text-blue-500">
                  <Target size={18} />
                </div>
                <div className="flex-grow">
                  <p className="font-bold text-charcoal">{item.name} - {item.media_name || 'Sin medio'}</p>
                  <p className="text-sm text-charcoal-muted">{item.email}</p>
                  {item.phone && <p className="text-xs text-charcoal-muted">{item.phone}</p>}
                  <p className="text-xs text-charcoal-muted flex items-center gap-1">
                    <Clock size={12} /> {new Date(item.created_at).toLocaleDateString('es-DO')}
                  </p>
                </div>
                <button className="p-2 hover:bg-white rounded-full shadow-sm border border-transparent hover:border-gray-200 transition-all">
                  <ArrowUpRight size={16} className="text-gray-400" />
                </button>
              </div>
            )) : (
              <div className="text-center py-8 text-charcoal-muted">
                <Target size={48} className="mx-auto mb-4 opacity-20" />
                <p>No hay actividad reciente</p>
              </div>
            )}
          </div>
        </div>

        <div className="bg-primary rounded-4xl p-8 text-white shadow-xl relative overflow-hidden">
          <div className="relative z-10">
            <h2 className="text-2xl font-bold mb-4">Próximo Workshop</h2>
            {upcomingWorkshop ? (
              <div className="bg-white/10 backdrop-blur-md rounded-3xl p-6 mb-6 border border-white/20">
                <p className="text-blue-100 text-xs font-bold uppercase tracking-widest mb-2">
                  {new Date(upcomingWorkshop.date).toLocaleDateString('es-DO', { month: 'long', day: 'numeric' })}
                </p>
                <h3 className="text-xl font-bold mb-4">{upcomingWorkshop.title}</h3>
                <div className="flex items-center gap-2 text-sm">
                  <Users size={16} /> Capacitación de Medios
                </div>
              </div>
            ) : (
              <div className="bg-white/10 backdrop-blur-md rounded-3xl p-6 mb-6 border border-white/20">
                <p className="text-blue-100 text-xs font-bold uppercase tracking-widest mb-2">Próximamente</p>
                <h3 className="text-xl font-bold mb-4">Estrategia de IA para Redacciones</h3>
                <div className="flex items-center gap-2 text-sm">
                  <Users size={16} /> Capacitación de Medios
                </div>
              </div>
            )}
            <button className="w-full py-4 bg-white text-primary font-bold rounded-2xl hover:bg-blue-50 transition-colors">
              Preparar Materiales
            </button>
          </div>
          <div className="absolute -bottom-12 -right-12 w-48 h-48 bg-white/10 rounded-full blur-3xl"></div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;